import React, { useContext, useMemo, useState } from 'react';
import { Button, Card, FeaturedIcon, Modal } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { AlertCircle, Calendar, DotsVertical, MarkerPin01 } from '@untitled-ui/icons-react';
import moment from 'moment';
import { toast } from 'react-toastify';

import { CalendarButtons } from 'src/components/CalendarButtons';
import DoctorIcon from 'src/assets/doctor.svg?react';
import { useClinicSelector, useDoctorSelector } from 'src/hooks/useAsyncSelector';
import { useSelector } from 'src/store';
import { bookingService } from 'src/services/booking';
import { clinicInfoContext } from 'src/contexts/clinicInfo';
import { clinicName, doctorName } from 'src/utils/name';
import { useNavigate } from 'react-router-dom';
import { stripHtml } from 'src/utils/html';

type Props = {
    appointmentId: number,
    appointmentTypeName: string,
    isCancellable?: boolean,
}

export function ManageBooking({ appointmentId, appointmentTypeName, isCancellable }: Props) {
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const navigate = useNavigate();

    const { data: doctor } = useDoctorSelector();
    const { data: clinic } = useClinicSelector();
    const { dateTime, clinicId: siteId } = useSelector(state => state.appointment);
    const { setOpen: setViewingClinicInfo } = useContext(clinicInfoContext);

    const { invoke: cancelBooking, isLoading: isCancelling } = useAsyncData({
        method: bookingService.CancelBooking,
        service: bookingService,
        invokeOnMount: false
    }, siteId, appointmentId)

    const appointmentTime = useMemo(() => moment(dateTime), [dateTime]);

    return <>
        <div className='mb-8'>
            <h1 className='text-gray-900 text-2xl font-semibold mb-2.5'>{clinic && `${clinicName(clinic)} Appointments`}</h1>
            <p className='text-gray-600 text-base font-normal'>
                Please note that appointments cannot be rescheduled, and most clinics require at least 24 hours' notice for cancellations.To reschedule or cancel, please contact the clinic directly.
            </p>
        </div>

        <Card.Shell>
            <Card.Body className='flex flex-row gap-3'>
                <figure className='w-10 h-10 rounded-full border overflow-hidden'>
                    <img className='block w-full h-full object-cover'
                        src={doctor?.umbracoProfileUrl ? `${import.meta.env.VITE_MYHEALTH_WEBSITE}${doctor.umbracoProfileUrl}` : doctor?.automedProfileUrl}
                    />
                    <figcaption className='absolute invisible'>
                        {doctorName(doctor)}
                    </figcaption>
                </figure>
                <div className='flex-1 text-sm text-gray-600'>
                    <h2 className='text-gray-700 font-semibold'>{doctorName(doctor)}</h2>
                    <h3>{doctor?.umbracoRole}</h3>
                </div>
                <Menu as="div" className="relative">
                    <MenuButton className='w-10 h-10 -mr-2 p-2 rounded-full data-[hover]:bg-gray-100 data-[open]:bg-gray-100'>
                        <DotsVertical className='w-6 h-6 text-gray-400' />
                    </MenuButton>
                    <MenuItems anchor="bottom end"
                        transition
                        className="bg-white border border-gray-200 shadow-lg w-60 rounded-lg mt-1 font-medium text-sm text-gray-700 origin-top transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                    >
                        <MenuItem>
                            <button
                                className="block w-full px-4 py-2.5 text-left data-[focus]:bg-gray-100"
                                onClick={() => setViewingClinicInfo(true)}
                            >
                                View details
                            </button>
                        </MenuItem>
                        <MenuItem>
                            <a className="block w-full px-4 py-2.5 text-left data-[focus]:bg-gray-100"
                                href={`tel:${clinic?.phoneNumber}`}>
                                Contact clinic
                            </a>
                        </MenuItem>
                    </MenuItems>
                </Menu>

            </Card.Body>
            <hr />

            <Card.Body>
                <ul className='text-gray-700 font-medium text-base space-y-4'>
                    <li className='flex flex-row gap-2.5'>
                        <DoctorIcon className='w-6 h-6 text-gray-400' />
                        {appointmentTypeName}
                    </li>
                    <li className='flex flex-row gap-2.5'>
                        <Calendar className='w-6 h-6 text-gray-400' />
                        {`${appointmentTime.format("dddd, D MMMM YYYY")} at ${appointmentTime.format("LT")}`}
                    </li>
                    <li className='flex flex-row gap-2.5'>
                        <MarkerPin01 className='w-6 h-6 text-gray-400' />
                        {clinic?.name}
                    </li>
                </ul>

                <div className='flex flex-col gap-y-1 mt-2.5'>
                    <CalendarButtons
                        address={clinic?.addressHtml ? stripHtml(clinic.addressHtml) : ''}
                        title={appointmentTypeName}
                        description={doctor && `Your appointment is with ${doctorName(doctor)}.`}
                        startDate={appointmentTime}
                        geo={clinic && { lat: clinic?.latitude, lng: clinic?.longitude }}
                    />
                    <Button color='error' className='mt-3' onClick={() => setShowCancelConfirm(true)} disabled={!isCancellable}>
                        Cancel Appointment
                    </Button>
                    {!isCancellable && <p className='mt-1 text-sm text-error-600'>This appointment is not cancellable. Please contact the clinic {clinic && <>on <a className='text-primary underline' href={`tel:${clinic?.phoneNumber}`}>{clinic?.phoneNumber}</a></>} for more information.</p>}
                </div>
            </Card.Body>
        </Card.Shell>

        <Modal open={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            hideX
            size='md'
        >
            <FeaturedIcon icon={AlertCircle}
                color='error' />
            <div className='text-gray-600 text-sm font-normal my-0'>
                <h3 className='mb-1 text-gray-900 font-semibold text-lg'>Cancel Appointment</h3>
                <p>Are you sure you want to cancel your appointment with {doctorName(doctor)} on <strong className='font-bold'>{`${appointmentTime.format("dddd, D MMMM YYYY")} at ${appointmentTime.format("LT")}`}</strong>?</p>
            </div>
            <div className='flex flex-col gap-y-1 mt-2.5'>
                <Button color='error'
                    className='mt-3'
                    disabled={isCancelling}
                    onClick={async () => {
                        await cancelBooking();
                        toast.success("Your booking has been cancelled")
                        setShowCancelConfirm(false);
                        navigate('/manage/cancelled', { replace: true })
                    }}
                >
                    Cancel Appointment
                </Button>
                <Button
                    color='gray'
                    className='mt-3'
                    onClick={() => setShowCancelConfirm(false)}>
                    No
                </Button>
            </div>
        </Modal>
    </>
}